<template>
  <CoreLayout>
    <CoreBread
      v-if="!isMobile"
      :items="breadcrumbs"
    />
    <div
      class="d-flex flex-column ga-8 ga-sm-10 mb-10"
      :class="{ 'container': !isMobile }"
    >
      <AnalysesHeroSection
        :town-name-pred="context.town?.name_pred || ''"
        :popular-analyses="popularTypes"
      />
      <AnalysesCategoriesSection
        :categories="categories"
        :class="{ 'container': isMobile }"
      />
      <AnalysesPopularSection
        :items="popularAnalyses"
        :class="{ 'container': isMobile }"
      />
      <AnalysesOnlineOrderSection
        v-if="context.is_analyses_appointment_available_in_town"
        :benefits="onlineOrderBenefits"
      />
      <LpuCardsSection />
      <AnalysesMetaH1Subtitle :class="{ 'container': isMobile }" />
    </div>
  </CoreLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { CoreLayout } from 'src/layouts'
import CoreBread from 'src/core/CoreBread/CoreBread.vue'
import AnalysesHeroSection
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/AnalysesHeroSection.vue'
import AnalysesCategoriesSection
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/AnalysesCategoriesSection.vue'
import AnalysesPopularSection
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/AnalysesPopularSection.vue'
import AnalysesOnlineOrderSection
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/AnalysesOnlineOrderSection.vue'
import LpuCardsSection
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsSection.vue'
import AnalysesMetaH1Subtitle
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/AnalysesMetaH1Subtitle.vue'
import mapAnalysesCategory
  from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/mapAnalysesCategory'
import mapPopularAnalysis
  from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/mapPopularAnalysis'
import mapPopularType
  from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/mapPopularType'
import { useContextStore, useURLStore } from 'src/store'
import type { AnalysesPageContext } from 'src/components/pages/[town-translit]/diagnostika/analiz/_types'
import type { CommonContextStore } from 'src/types/context'
import type {
  OnlineOrderBenefit,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/components/types'

const { context, urlLang } = useContextStore() as CommonContextStore & {
  context: AnalysesPageContext
}
const { src: staticPath } = context.global.static
const { siteNamePD } = context.global.sites
const { reverseFull, reverseFullL10N } = useURLStore()

const isMobile = context.flavour === 'mobile'

const breadcrumbs = [
  {
    name: context.request.country.is_russia ? 'ПроДокторов' : 'YDoc',
    href: reverseFull({ name: 'prodoctorov_main' }),
  },
  {
    name: context.town.name,
    href: reverseFullL10N({
      name: 'town',
      args: {
        town_translit: context.town.translit,
        language: urlLang,
      },
    }),
  },
  {
    name: context.service_class.group === 'uslugi' ? 'Услуги' : 'Диагностика',
    href: new URL(`/${context.town.translit}/${context.service_class.group}/`, siteNamePD).href,
  },
]

const ONLINE_ORDER_IMAGE_BASE = `${staticPath}_v1/pd/pictures/analyzes/online-appointment`

const categories = computed(() => (
  (context.analyses_categories || []).map(item => mapAnalysesCategory(
    item,
    categoryTranslit => reverseFull({
      name: 'services_category',
      args: [
        context.town?.translit || '',
        context.service_class.group,
        context.service_class.translit,
        categoryTranslit,
      ],
    }),
  ))
))

const popularAnalyses = computed(() => (
  (context.popular_analyses || []).map(item => mapPopularAnalysis(item, context.town.translit))
))

const popularTypes = computed(() => (
  (context.popular_types || []).map(mapPopularType)
))

const onlineOrderBenefits: OnlineOrderBenefit[] = [
  {
    number: 1,
    title: 'Быстро',
    description: 'Не нужно стоять в очереди для заказа и оплаты',
    image: `${ONLINE_ORDER_IMAGE_BASE}/bystro.png`,
  },
  {
    number: 2,
    title: 'Выгодно',
    description: 'Можно оплатить со скидкой',
    image: `${ONLINE_ORDER_IMAGE_BASE}/vygodno.png`,
  },
  {
    number: 3,
    title: 'Конфиденциально',
    description: 'Другие пациенты не узнают, какие анализы вы сдаёте',
    image: `${ONLINE_ORDER_IMAGE_BASE}/konfidencialno.png`,
  },
]
</script>
